import { useCallback, useEffect, useRef } from 'react';

type CleanupFunction = () => void;
/**
 * Custom hook to run a callback function if the component is still mounted.
 * This helps prevent memory leaks and errors from trying to update state on unmounted components.
 *
 * @returns A function that runs a callback if the component is still mounted and returns a cleanup function.
 */
export const useMounted = () => {
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isMounted = () => mountedRef.current;

  const runIfMounted = useCallback((callback: (abortController: AbortController) => void): CleanupFunction => {
    if (!isMounted()) return () => {};

    const abortController = new AbortController();
    callback(abortController);

    return () => {
      abortController.abort();
    };
  }, []);

  return { isMounted, runIfMounted };
};
